export const reducerName = 'LEAGUES_CONFIGS'

export const BASE_CONFIGS_OPTIONS = [
  { id: 'copy_player_roster' },
  { id: 'tournament_library' },
  { id: 'copy_logos', withInfo: true },
  { id: 'copy_dft' },
  { id: 'copy_all_portal_pages', withInfo: true },
  { id: 'rounds_and_series' },
  { id: 'open_registration' },
  { id: 'stripe_account' },
  { id: 'tournaments', dependencies: [ 'copy_dft', 'rounds_and_series' ], withInfo: true },
  { id: 'registration_payment_options', dependencies: [ 'open_registration' ] },
  { id: 'copy_managers', capabilityFlag: 'has_managers' },
  { id: 'golf_hub_page', capabilityFlag: 'golf_hub_page' },
]

// Maps option IDs to the league capability flag that must be present on at least one
// selected league for the option to be shown. Options without an entry are always shown.
export const OPTION_CAPABILITY_FLAGS = BASE_CONFIGS_OPTIONS.reduce((acc, option) => {
  if (option.capabilityFlag) {
    acc[option.id] = option.capabilityFlag
  }
  return acc
}, {})

const CONFIGS_OPTIONS_WITH_DEPENDENCIES =
  BASE_CONFIGS_OPTIONS.filter(configOption => configOption.dependencies)

export const EMPTY_CONFIGS_OPTIONS = {}
export const COMPLETE_CONFIGS_OPTIONS =
  Object.fromEntries(BASE_CONFIGS_OPTIONS.map(option => [ option.id, true ]))

export const getSelectedOptionsNames = (configOptions) =>
  Object.entries(configOptions).filter(( [ , val ] ) => val)
    .map(( [ opt ] ) => opt)

export const isMissingDependenciesConfigsOption = (targetConfigsOption, configsOptions) => {
  return !(new Set(targetConfigsOption.dependencies).isSubsetOf(new Set(getSelectedOptionsNames(configsOptions))))
}

// Some options depend on other options. Make sure that if the dependencies are missing for some option, it is disabled
// The options CANNOT be enabled
const getSanitizedConfigOptions = (configsOptions) => {
  CONFIGS_OPTIONS_WITH_DEPENDENCIES.forEach(option => {
    if (isMissingDependenciesConfigsOption(option, configsOptions)) {
      configsOptions[option.id] = false
    }
  })

  return configsOptions
}


const ActionTypes = {
  SET_CONFIGS_OPTIONS: 'SET_CONFIGS_OPTIONS',
  SET_CONFIGS_SEASON: 'SET_CONFIGS_SEASON',
}

const initialState = {
  // The selected config options
  configsOptions: {},
  // Season
  season: {},
}

const LeaguesConfigsReducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.SET_CONFIGS_SEASON:
      return {
        ...state,
        season: action.season,
      }

    case ActionTypes.SET_CONFIGS_OPTIONS:
      return {
        ...state,
        configsOptions: getSanitizedConfigOptions(action.configsOptions),
      }

    default:
      return { ... state }
  }
}

const setConfigsOptionsAction = configsOptions => ({
  type: ActionTypes.SET_CONFIGS_OPTIONS,
  configsOptions: configsOptions,
})

export const setConfigsOptions = (configsOptions) => (dispatch) => {
  dispatch(setConfigsOptionsAction(configsOptions))
}

const setConfigsSeasonAction = season => ({
  type: ActionTypes.SET_CONFIGS_SEASON,
  season: season,
})

export const setConfigsSeason = (season) => (dispatch) => {
  dispatch(setConfigsSeasonAction(season))
}

export const selectConfigsOptions = state => state?.[reducerName]?.configsOptions
export const selectConfigsSeason = state => state?.[reducerName]?.season

// Returns the subset of BASE_CONFIGS_OPTIONS relevant to the given leagues.
// Conditional options (with a capabilityFlag) are only included if at least one league
// carries that flag as truthy. Use via the useAvailableConfigOptions hook, not directly.
export const getAvailableConfigOptions = (enhancedLeagues = []) =>
  BASE_CONFIGS_OPTIONS.filter(option => {
    const flag = OPTION_CAPABILITY_FLAGS[option.id]
    if (!flag) {
      return true
    }
    return enhancedLeagues.some(league => league[flag])
  })

export const namedReducer = { [reducerName]: LeaguesConfigsReducer }

export const selectIsRoundsAndSeriesSelected = state => !!state?.[reducerName]?.configsOptions?.rounds_and_series

export const getInitialState = (cloneLeaguesSession) => {
  const sessionData = cloneLeaguesSession?.data
  const firstLeague = sessionData?.leagues
    ? Object.values(sessionData.leagues)[0]
    : null

  const configsOptions = BASE_CONFIGS_OPTIONS.reduce((acc, option) => {
    acc[option.id] = firstLeague?.[option.id] ?? false
    return acc
  }, {})

  return {
    configsOptions: getSanitizedConfigOptions(configsOptions),
    season: firstLeague?.season ?? {},
  }
}

export default LeaguesConfigsReducer
